import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { FileText, Tag, Image, Search, HelpCircle, Info, X } from "lucide-react";
import { useTranslation } from "../i18n/hooks";

const HelpTopicCard = () => {
  const { t } = useTranslation();
  
  const helpTopics = [
    {
      icon: FileText,
      title: t('helpTopics.product_titles_topic'),
      description: t('helpTopics.product_titles_description')
    },
    {
      icon: FileText,
      title: t('helpTopics.product_descriptions_topic'),
      description: t('helpTopics.product_descriptions_description')
    },
    {
      icon: Tag,
      title: t('helpTopics.alt_text_topic'),
      description: t('helpTopics.alt_text_description')
    },
    {
      icon: Search,
      title: t('helpTopics.seo_topic'),
      description: t('helpTopics.seo_description')
    },
    {
      icon: Image,
      title: t('helpTopics.visual_content_topic'),
      description: t('helpTopics.visual_content_description')
    }
  ];

  const [selectedTopic, setSelectedTopic] = useState<{
    icon: React.ElementType;
    title: string;
    description: string
  } | null>(null);

  return (
    <>
      <Card className="border-blue-100 shadow-sm">
        <CardHeader className="pb-3 space-y-1">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-500" />
            <CardTitle className="text-base font-semibold text-gray-800">{t('helpTopics.title')}</CardTitle>
          </div>
          <CardDescription className="text-sm text-gray-600">{t('helpTopics.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {helpTopics.map((topic, index) => (
            <Button
              key={index}
              variant="ghost"
              className="w-full justify-between p-3 h-auto hover:bg-blue-50/80 group transition-colors duration-200 border-0"
              onClick={() => setSelectedTopic(topic)}
            >
              <div className="flex items-center gap-3 min-w-0">
                <topic.icon className="w-5 h-5 text-blue-500 shrink-0" />
                <span className="text-left text-sm text-gray-700 font-medium truncate">
                  {topic.title}
                </span>
              </div>
              <Info className="w-4 h-4 text-blue-400 shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
            </Button>
          ))}
        </CardContent>
      </Card>

      {/* Modal Dialog */}
      {selectedTopic && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl my-8 shadow-lg">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-3 pr-8">
                  <selectedTopic.icon className="w-6 h-6 text-blue-500 shrink-0 mt-1" />
                  <h2 className="text-xl font-semibold text-gray-900 leading-tight">
                    {selectedTopic.title}
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedTopic(null)}
                  className="h-8 w-8 rounded-full hover:bg-gray-100"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </Button>
              </div>

              {/* Content */}
              <div className="space-y-6">
                <div className="prose prose-gray max-w-none">
                  {selectedTopic.title === t('helpTopics.product_titles_topic') && (
                    <>
                      <p className="text-gray-700 text-base leading-relaxed">{t('helpTopics.product_titles_description').split('.')[0]}.</p>
                      
                      <div className="bg-blue-50 rounded-lg p-4 my-4">
                        <h3 className="text-sm font-semibold text-blue-900 mb-2">{t('helpTopics.key_components')}:</h3>
                        <ul className="text-sm text-blue-800 space-y-1 list-disc pl-4">
                          <li>Brand name</li>
                          <li>Model</li>
                          <li>Size</li>
                          <li>Color</li>
                          <li>Key features</li>
                        </ul>
                      </div>

                      <div className="space-y-3">
                        <p className="text-gray-700">{t('helpTopics.front_load_description')}</p>
                        
                        <div className="grid grid-cols-1 gap-3 mt-4">
                          <div className="rounded-md bg-red-50 p-3">
                            <p className="text-sm font-medium text-red-800 mb-1">{t('helpTopics.bad_example')}</p>
                            <p className="text-sm text-red-700">"Nice Blue Shirt"</p>
                          </div>
                          
                          <div className="rounded-md bg-green-50 p-3">
                            <p className="text-sm font-medium text-green-800 mb-1">{t('helpTopics.good_example')}</p>
                            <p className="text-sm text-green-700">"Levi's 501 Straight-Fit Men's Jeans - Dark Blue Denim, Stone Wash"</p>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {selectedTopic.title === t('helpTopics.product_descriptions_topic') && (
                    <>
                      <p className="text-gray-700 text-base leading-relaxed mb-4">{t('helpTopics.product_descriptions_description').split('.')[0]}.</p>
                      
                      <div className="bg-blue-50 rounded-lg p-4 my-4">
                        <h3 className="text-sm font-semibold text-blue-900 mb-2">{t('helpTopics.writing_guidelines')}</h3>
                        <ul className="text-sm text-blue-800 space-y-1 list-disc pl-4">
                          <li>Start with the big benefit</li>
                          <li>Include vital specs (materials, dimensions)</li>
                          <li>Add real-world uses and situations</li>
                          <li>Use short sentences</li>
                          <li>Break up text with bullet points</li>
                        </ul>
                      </div>

                      <div className="mt-4">
                        <p className="text-gray-700 mb-2">{t('helpTopics.people_buy_outcomes')}</p>
                        <p className="text-gray-700">{t('helpTopics.weak_vs_strong_description')}</p>
                      </div>
                    </>
                  )}

                  {selectedTopic.title === t('helpTopics.alt_text_topic') && (
                    <>
                      <p className="text-gray-700 text-base leading-relaxed mb-4">{t('helpTopics.alt_text_description').split('.').slice(0, 2).join('.')}.</p>
                      
                      <div className="grid grid-cols-1 gap-3 my-4">
                        <div className="rounded-md bg-red-50 p-3">
                          <p className="text-sm font-medium text-red-800 mb-1">{t('helpTopics.poor_alt_text')}</p>
                          <p className="text-sm text-red-700">"blue shirt"</p>
                        </div>
                        
                        <div className="rounded-md bg-green-50 p-3">
                          <p className="text-sm font-medium text-green-800 mb-1">{t('helpTopics.better_alt_text')}</p>
                          <p className="text-sm text-green-700">"Men's Royal Blue Oxford Cotton Dress Shirt with Button-Down Collar"</p>
                        </div>
                      </div>

                      <p className="text-gray-700">{t('helpTopics.missing_alt_text')}</p>
                    </>
                  )}

                  {selectedTopic.title === t('helpTopics.seo_topic') && (
                    <>
                      <p className="text-gray-700 text-base leading-relaxed">{t('helpTopics.seo_description').split('.').slice(0, 2).join('.')}.</p>
                      
                      <div className="bg-blue-50 rounded-lg p-4 my-4">
                        <h3 className="text-sm font-semibold text-blue-900 mb-2">{t('helpTopics.key_seo_elements')}</h3>
                        <ul className="text-sm text-blue-800 space-y-1 list-disc pl-4">
                          <li>Meta descriptions</li>
                          <li>Proper heading structure</li>
                          <li>Relevant keywords in natural language</li>
                        </ul>
                      </div>

                      <p className="text-gray-700 mt-4">{t('helpTopics.seo_description').split('.').slice(2).join('.')}.</p>
                    </>
                  )}

                  {selectedTopic.title === t('helpTopics.visual_content_topic') && (
                    <>
                      <p className="text-gray-700 text-base leading-relaxed mb-4">{t('helpTopics.visual_content_description').split('.').slice(0, 2).join('.')}.</p>
                      
                      <div className="bg-blue-50 rounded-lg p-4 my-4">
                        <h3 className="text-sm font-semibold text-blue-900 mb-2">{t('helpTopics.essential_photo_elements')}</h3>
                        <ul className="text-sm text-blue-800 space-y-1 list-disc pl-4">
                          <li>Multiple angles</li>
                          <li>Lifestyle shots showing real use</li>
                          <li>Zoom capability for detail shots</li>
                          <li>Clear, bright, professional images</li>
                        </ul>
                      </div>

                      <p className="text-gray-700">{t('helpTopics.visual_content_description').split('.').slice(2, -1).join('.')}.</p>
                      
                      <p className="text-gray-700 font-medium mt-4">{t('helpTopics.visual_content_description').split('.').slice(-1)[0]}.</p>
                    </>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="mt-8 flex justify-end">
                <Button 
                  onClick={() => setSelectedTopic(null)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2"
                >
                  {t('helpTopics.got_it')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpTopicCard;